import * as THREE from 'three';
import { toCreasedNormals } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

type AnyMaterial = THREE.Material & {
  color?: THREE.Color;
  emissive?: THREE.Color;
  emissiveIntensity?: number;
  envMapIntensity?: number;
  roughness?: number;
  metalness?: number;
  specularIntensity?: number;
  clearcoat?: number;
  sheen?: number;
  transmission?: number;
};

export interface PreparedModel {
  ledMaterials: AnyMaterial[];
  /** Vertex positions (model space) belonging to the LED ring */
  ledPoints: THREE.Vector3[];
}

const MAX_POINTS = 6000;

function collectPoints(mesh: THREE.Mesh, ledIndices: Set<number>, out: THREE.Vector3[]) {
  const pos = mesh.geometry.attributes.position;
  if (!pos) return;
  const index = mesh.geometry.index;
  const groups = Array.isArray(mesh.material) ? mesh.geometry.groups : [];
  const ranges =
    groups.length > 0
      ? groups.filter((g) => ledIndices.has(g.materialIndex ?? 0)).map((g) => [g.start, g.start + g.count])
      : [[0, index ? index.count : pos.count]];

  const total = ranges.reduce((s, [a, b]) => s + (b - a), 0);
  const step = Math.max(1, Math.floor(total / MAX_POINTS));
  ranges.forEach(([start, end]) => {
    for (let i = start; i < end; i += step) {
      const vi = index ? index.getX(i) : i;
      out.push(new THREE.Vector3().fromBufferAttribute(pos, vi).applyMatrix4(mesh.matrixWorld));
    }
  });
}

const LED_NAME = /led|light|glow|emis|red/i;

function isGlass(m: AnyMaterial): boolean {
  return m.transparent || m.opacity < 1 || (m.transmission ?? 0) > 0;
}

function isRedLed(m: AnyMaterial): boolean {
  const c = m.color;
  const reddish = !!c && c.r > 0.2 && c.r > c.g * 2 && c.r > c.b * 2;
  return reddish || (LED_NAME.test(m.name) && !!c && c.r > c.g);
}

function luminance(c?: THREE.Color): number {
  return c ? 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b : 0.5;
}

/**
 * Removes reflections from the glass shell and lens, keeps the body readable
 * under soft light, and makes the red LED ring glow.
 */
export function prepareModel(model: THREE.Object3D): PreparedModel {
  const ledMaterials: AnyMaterial[] = [];
  const ledPoints: THREE.Vector3[] = [];
  const softMeshes: THREE.Mesh[] = [];

  model.updateMatrixWorld(true);
  model.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) as AnyMaterial[];
    const ledIndices = new Set<number>();

    mats.forEach((m, matIndex) => {
      if (isGlass(m)) {
        // Glass shell: fully matte — no mirrored environment, no light hotspots
        m.envMapIntensity = 0;
        if (m.roughness !== undefined) m.roughness = 1;
        if (m.metalness !== undefined) m.metalness = 0;
        if (m.specularIntensity !== undefined) m.specularIntensity = 0;
        if (m.clearcoat !== undefined) m.clearcoat = 0;
        if (m.sheen !== undefined) m.sheen = 0;
        m.depthWrite = false;
        m.needsUpdate = true;
      } else if (isRedLed(m) && m.emissive) {
        m.emissive = new THREE.Color('#ff2414');
        m.emissiveIntensity = 2.2;
        ledMaterials.push(m);
        ledIndices.add(matIndex);
      } else if (luminance(m.color) < 0.06) {
        // Dark lens / face surfaces: satin finish so nothing mirrors on the face
        if (m.roughness !== undefined) m.roughness = Math.max(m.roughness, 0.7);
        if (m.clearcoat !== undefined) m.clearcoat = 0;
        if (m.specularIntensity !== undefined) m.specularIntensity = 0.2;
        m.envMapIntensity = 0.5;
        m.needsUpdate = true;
      } else {
        // Body parts: semi-gloss / matte metal — rough enough to scatter light smoothly
        if (m.roughness !== undefined) m.roughness = THREE.MathUtils.clamp(m.roughness, 0.48, 0.78);
        if (m.metalness !== undefined) m.metalness = Math.min(m.metalness, 0.75);
        if (m.clearcoat !== undefined) m.clearcoat = 0;
        if (m.specularIntensity !== undefined) m.specularIntensity = Math.min(m.specularIntensity, 0.6);
        m.envMapIntensity = 0.9;
        m.needsUpdate = true;
      }
    });

    if (ledIndices.size > 0) collectPoints(mesh, ledIndices, ledPoints);
    else if (!mats.some(isGlass)) softMeshes.push(mesh);
  });

  // Soften hard, faceted edges: smooth normals across shallow angles,
  // keep only genuinely sharp creases.
  softMeshes.forEach((mesh) => {
    const geo = mesh.geometry;
    if (!geo.attributes.position || geo.morphAttributes.position || (mesh as THREE.SkinnedMesh).isSkinnedMesh) return;
    try {
      mesh.geometry = toCreasedNormals(geo, THREE.MathUtils.degToRad(55));
      geo.dispose();
    } catch {
      // Leave geometry untouched if it can't be processed
    }
  });

  return { ledMaterials, ledPoints };
}
